import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import {
  listLocationsByType,
  listSubLocationsByTypeAndLocation,
  listMakesAndModelsByTypeAndLocation,
  listPoesByTypeLocationSubLocationAndMake,
  listIntervalsByTypeMakeAndLocation,
  appendSolarGenerationNodeToPipeline,
  editSolarGenerationNode
} from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './SolarGenerationConfigModal.css';

Modal.setAppElement('#root');

const UNSPECIFIED = '__none__';
const formatMetaOption = (value) =>
  value === UNSPECIFIED ? 'Unspecified (legacy)' : value;

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  }
};

const SolarGenerationConfigModal = ({ isOpen, onClose, onConfigured, initialData = {} }) => {
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [btmFlag, setBtmFlag] = useState(initialData.btmFlag ?? false);
  const [isTest, setIsTest] = useState(
    initialData.is_test !== undefined ? !!initialData.is_test : true
  );

  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [makeAndModels, setMakeAndModels] = useState([]);
  const [poes, setPoes] = useState([]);
  const [intervals, setIntervals] = useState([]);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedSubLocation, setSelectedSubLocation] = useState('');
  const [selectedMakeAndModel, setSelectedMakeAndModel] = useState('');
  const [selectedPoe, setSelectedPoe] = useState('');
  const [selectedInterval, setSelectedInterval] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { session } = usePipeline();
  const isEditing = !!initialData.name && initialData.isConfigured === true;

  const resetCascadeBelowLocation = () => {
    setSelectedSubLocation('');
    setSelectedMakeAndModel('');
    setSelectedPoe('');
    setSelectedInterval('');
    setSubLocations([]);
    setMakeAndModels([]);
    setPoes([]);
    setIntervals([]);
  };

  useEffect(() => {
    if (isOpen) {
      setBtmFlag(initialData.btmFlag ?? false);
      setIsTest(initialData.is_test !== undefined ? !!initialData.is_test : true);
      setNodeName(initialData.name || '');
      setSelectedLocation('');
      resetCascadeBelowLocation();
      setSubmitError(null);
    }
  }, [isOpen, initialData.btmFlag, initialData.is_test, initialData.name]);

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listLocationsByType('solar', isTest);
      setLocations(data?.locations || []);
    } catch (error) {
      console.error('Error fetching solar locations:', error);
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  }, [isTest]);

  const fetchSubLocations = useCallback(async () => {
    if (!selectedLocation) return;
    setIsLoading(true);
    try {
      const data = await listSubLocationsByTypeAndLocation('solar', selectedLocation, isTest);
      setSubLocations(data?.sub_locations || []);
    } catch (error) {
      console.error('Error fetching sub-locations:', error);
      setSubLocations([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation, isTest]);

  const fetchMakeAndModels = useCallback(async () => {
    if (!selectedLocation || !selectedSubLocation) return;
    setIsLoading(true);
    try {
      const data = await listMakesAndModelsByTypeAndLocation(
        'solar',
        selectedLocation,
        isTest,
        selectedSubLocation
      );
      setMakeAndModels(data?.makes_and_models || []);
    } catch (error) {
      console.error(`Error fetching make and models for location ${selectedLocation}:`, error);
      setMakeAndModels([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation, selectedSubLocation, isTest]);

  const fetchPoes = useCallback(async () => {
    if (!selectedLocation || !selectedSubLocation || !selectedMakeAndModel) return;
    setIsLoading(true);
    try {
      const data = await listPoesByTypeLocationSubLocationAndMake(
        'solar',
        selectedLocation,
        selectedMakeAndModel,
        isTest,
        selectedSubLocation
      );
      setPoes(data?.poes || []);
    } catch (error) {
      console.error('Error fetching PoE values:', error);
      setPoes([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation, selectedSubLocation, selectedMakeAndModel, isTest]);

  const fetchIntervals = useCallback(async () => {
    if (!selectedMakeAndModel || !selectedLocation || !selectedSubLocation || !selectedPoe) return;
    setIsLoading(true);
    try {
      const data = await listIntervalsByTypeMakeAndLocation(
        'solar',
        selectedMakeAndModel,
        selectedLocation,
        isTest,
        selectedSubLocation,
        selectedPoe
      );
      setIntervals(data?.intervals || []);
    } catch (error) {
      console.error(`Error fetching intervals for ${selectedMakeAndModel} at ${selectedLocation}:`, error);
      setIntervals([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMakeAndModel, selectedLocation, selectedSubLocation, selectedPoe, isTest]);

  useEffect(() => {
    if (isOpen) {
      setSelectedLocation('');
      resetCascadeBelowLocation();
      fetchLocations();
    }
  }, [isOpen, isTest, fetchLocations]);

  useEffect(() => {
    if (selectedLocation) {
      setSelectedSubLocation('');
      setSelectedMakeAndModel('');
      setSelectedPoe('');
      setSelectedInterval('');
      setMakeAndModels([]);
      setPoes([]);
      setIntervals([]);
      fetchSubLocations();
    }
  }, [selectedLocation, fetchSubLocations]);

  useEffect(() => {
    if (selectedSubLocation) {
      setSelectedMakeAndModel('');
      setSelectedPoe('');
      setSelectedInterval('');
      setPoes([]);
      setIntervals([]);
      fetchMakeAndModels();
    }
  }, [selectedSubLocation, fetchMakeAndModels]);

  useEffect(() => {
    if (selectedMakeAndModel && selectedSubLocation) {
      setSelectedPoe('');
      setSelectedInterval('');
      setIntervals([]);
      fetchPoes();
    }
  }, [selectedMakeAndModel, selectedSubLocation, fetchPoes]);

  useEffect(() => {
    if (selectedPoe && selectedMakeAndModel) {
      setSelectedInterval('');
      fetchIntervals();
    }
  }, [selectedPoe, selectedMakeAndModel, fetchIntervals]);

  useEffect(() => {
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  const handleToggleIsTest = (nextIsTest) => {
    if (nextIsTest === isTest) return;
    setIsTest(nextIsTest);
  };

  const handleConfigure = async () => {
    let configData = null;

    try {
      setSubmitError(null);
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        setSubmitError({ title: 'Invalid node name', message: nameValidation.errorMessage });
        return;
      }

      if (!selectedLocation) {
        setSubmitError({ title: 'Missing selection', message: 'Please select a location.' });
        return;
      }
      if (!selectedSubLocation) {
        setSubmitError({ title: 'Missing selection', message: 'Please select a sub-location.' });
        return;
      }
      if (!selectedMakeAndModel) {
        setSubmitError({ title: 'Missing selection', message: 'Please select a make and model.' });
        return;
      }
      if (!selectedPoe) {
        setSubmitError({ title: 'Missing selection', message: 'Please select a PoE.' });
        return;
      }
      if (!selectedInterval) {
        setSubmitError({ title: 'Missing selection', message: 'Please select an interval.' });
        return;
      }

      setIsSubmitting(true);

      const position = initialData.position || { x: 0, y: 0 };

      if (session.pipeline_name && session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          if (isEditing) {
            const response = await editSolarGenerationNode(
              session.pipeline_name,
              initialData.name,
              selectedMakeAndModel,
              selectedLocation,
              selectedInterval,
              btmFlag,
              isTest,
              selectedSubLocation,
              selectedPoe
            );
            configData = {
              name: initialData.name,
              type: 'solar',
              makeAndModel: selectedMakeAndModel,
              location: selectedLocation,
              sub_location: selectedSubLocation,
              poe: selectedPoe,
              interval: selectedInterval,
              file_path: response?.file_path,
              btmFlag,
              is_test: isTest,
            };
          } else {
            const response = await appendSolarGenerationNodeToPipeline(
              session.pipeline_name,
              nodeName,
              selectedMakeAndModel,
              selectedLocation,
              selectedInterval,
              btmFlag,
              position.x,
              position.y,
              isTest,
              selectedSubLocation,
              selectedPoe
            );
            if (response && response.file_path) {
              configData = {
                name: nodeName,
                type: 'solar',
                makeAndModel: selectedMakeAndModel,
                location: selectedLocation,
                sub_location: selectedSubLocation,
                poe: selectedPoe,
                interval: selectedInterval,
                file_path: response.file_path,
                btmFlag,
                is_test: isTest,
              };
            }
          }
        } catch (apiError) {
          console.error('Error calling solar generation API:', apiError);
          const info = notifyError(apiError, {
            operation: isEditing ? OP.node.editSolarGeneration : OP.node.appendSolarGeneration,
            showToast: false,
          });
          setSubmitError(info);
          setIsSubmitting(false);
          return;
        }
      }

      if (configData) {
        onConfigured(configData);
      } else {
        onConfigured({
          name: isEditing ? initialData.name : nodeName,
          type: 'solar',
          makeAndModel: selectedMakeAndModel,
          location: selectedLocation,
          sub_location: selectedSubLocation,
          poe: selectedPoe,
          interval: selectedInterval,
          btmFlag,
          is_test: isTest,
        });
      }

      setIsSubmitting(false);
    } catch (error) {
      console.error('Error configuring solar generation node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    !isSubmitting &&
    (isEditing || (nodeName && !nodeNameError)) &&
    selectedLocation &&
    selectedSubLocation &&
    selectedMakeAndModel &&
    selectedPoe &&
    selectedInterval;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Solar Generation Configuration"
    >
      <div className="solar-modal">
        <button onClick={onClose} className="solar-modal-close">
          <IoCloseSharp size={24} />
        </button>

        <h2 className="solar-modal-title">{isEditing ? 'Edit Solar Generation' : 'Solar Generation Configuration'}</h2>

        {submitError && (
          <div
            role="alert"
            className="solar-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}

        <div className="solar-modal-field">
          <label className="solar-modal-label">Data source:</label>
          <div className="solar-data-mode-toggle" role="group" aria-label="Data source">
            <button
              type="button"
              className={`solar-data-mode-btn ${isTest ? 'active' : ''}`}
              onClick={() => handleToggleIsTest(true)}
            >
              Test data
            </button>
            <button
              type="button"
              className={`solar-data-mode-btn ${!isTest ? 'active' : ''}`}
              onClick={() => handleToggleIsTest(false)}
            >
              Actual data
            </button>
          </div>
          {!isTest && locations.length === 0 && !isLoading && (
            <div className="solar-modal-info">
              No actual profiles uploaded yet. Upload data from the header (Actual tab), or switch back to Test data.
            </div>
          )}
        </div>

        <div className="solar-modal-field">
          <label className="solar-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          <input
            type="text"
            value={nodeName}
            onChange={(e) => setNodeName(e.target.value)}
            onBlur={() => {
              const validation = validateNodeName(nodeName);
              setNodeNameError(validation.isValid ? '' : validation.errorMessage);
            }}
            className={`solar-modal-input ${nodeNameError ? 'error' : ''}`}
            placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
            aria-invalid={!!nodeNameError}
            aria-describedby={nodeNameError ? "node-name-error" : undefined}
            disabled={isEditing}
            title={isEditing ? 'Node name cannot be changed in edit mode' : undefined}
          />
          {nodeNameError && (
            <div className="solar-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!nodeNameError && (
            <div className="solar-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>

        <div className="solar-modal-field">
          <label className="solar-modal-label">
            Location: <span className="required">*</span>
          </label>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="solar-modal-input"
            disabled={isLoading || locations.length === 0}
            aria-required="true"
          >
            <option value="">Select Location</option>
            {locations.map((location) => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
          {isLoading && locations.length === 0 && (
            <div className="solar-modal-loading">Loading locations...</div>
          )}
          {locations.length === 0 && !isLoading && (
            <div className="solar-modal-no-devices">No locations available</div>
          )}
        </div>

        <div className="solar-modal-field">
          <label className="solar-modal-label">
            Sub-location: <span className="required">*</span>
          </label>
          <select
            value={selectedSubLocation}
            onChange={(e) => setSelectedSubLocation(e.target.value)}
            className="solar-modal-input"
            disabled={isLoading || !selectedLocation || subLocations.length === 0}
            aria-required="true"
          >
            <option value="">Select Sub-location</option>
            {subLocations.map((sub) => (
              <option key={sub} value={sub}>{formatMetaOption(sub)}</option>
            ))}
          </select>
          {!selectedLocation && (
            <div className="solar-modal-info">Please select a location first</div>
          )}
          {selectedLocation && subLocations.length === 0 && !isLoading && (
            <div className="solar-modal-no-devices">No sub-locations available for this location</div>
          )}
        </div>

        <div className="solar-modal-field">
          <label className="solar-modal-label">
            Make and Model: <span className="required">*</span>
          </label>
          <select
            value={selectedMakeAndModel}
            onChange={(e) => setSelectedMakeAndModel(e.target.value)}
            className="solar-modal-input"
            disabled={isLoading || !selectedSubLocation || makeAndModels.length === 0}
            aria-required="true"
          >
            <option value="">Select Make and Model</option>
            {makeAndModels.map((makeAndModel) => (
              <option key={makeAndModel} value={makeAndModel}>{makeAndModel}</option>
            ))}
          </select>
          {!selectedSubLocation && (
            <div className="solar-modal-info">Please select a sub-location first</div>
          )}
          {selectedSubLocation && makeAndModels.length === 0 && !isLoading && (
            <div className="solar-modal-no-devices">No make and models available for this sub-location</div>
          )}
        </div>

        <div className="solar-modal-field">
          <label className="solar-modal-label">
            PoE: <span className="required">*</span>
          </label>
          <select
            value={selectedPoe}
            onChange={(e) => setSelectedPoe(e.target.value)}
            className="solar-modal-input"
            disabled={isLoading || !selectedMakeAndModel || poes.length === 0}
            aria-required="true"
          >
            <option value="">Select PoE</option>
            {poes.map((poeValue) => (
              <option key={poeValue} value={poeValue}>{formatMetaOption(poeValue)}</option>
            ))}
          </select>
          {!selectedMakeAndModel && (
            <div className="solar-modal-info">Please select a make and model first</div>
          )}
          {selectedMakeAndModel && poes.length === 0 && !isLoading && (
            <div className="solar-modal-no-devices">No PoE values available for this selection</div>
          )}
        </div>

        <div className="solar-modal-field">
          <label className="solar-modal-label">
            Interval: <span className="required">*</span>
          </label>
          <select
            value={selectedInterval}
            onChange={(e) => setSelectedInterval(e.target.value)}
            className="solar-modal-input"
            disabled={isLoading || !selectedPoe || intervals.length === 0}
            aria-required="true"
          >
            <option value="">Select Interval</option>
            {intervals.map((interval) => (
              <option key={interval} value={interval}>
                {interval === 15 ? '15 mins' : interval === 60 ? '1 hr' : `${interval} mins`}
              </option>
            ))}
          </select>
          {!selectedPoe && (
            <div className="solar-modal-info">Please select a PoE first</div>
          )}
          {selectedPoe && intervals.length === 0 && !isLoading && (
            <div className="solar-modal-no-devices">No intervals available for this selection</div>
          )}
        </div>

        <div className="solar-modal-field">
          <label className="solar-modal-label">
            Behind-the-meter (BTM):
          </label>
          <div className="solar-modal-toggle">
            <label>
              <input
                type="checkbox"
                checked={btmFlag}
                onChange={(e) => setBtmFlag(e.target.checked)}
              />
              <span style={{ marginLeft: 8 }}>This solar plant is BTM</span>
            </label>
          </div>
        </div>

        <div className="solar-modal-button-container">
          <button
            className="solar-modal-configure-button"
            onClick={handleConfigure}
            disabled={!canSubmit}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : isEditing ? 'Save Changes' : 'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SolarGenerationConfigModal;
